import React, { useState, useCallback } from "react";
import {
  Box,
  TextField,
  FormControl,
  Select,
  MenuItem,
  Typography,
  Button,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Autocomplete,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import { debounce } from "lodash";
import {
  IKit,
  IKitComposicao,
  ISelectOption,
  TIPO_PRODUTO_OPTIONS,
} from "../types/kit.types";
import {
  buscarPerfisComposicao,
  buscarComponentesComposicao,
  IOpcaoComposicao,
} from "../services/kitService";

interface TabComposicaoProps {
  kit: Partial<IKit>;
  onChange: (updates: Partial<IKit>) => void;
  unidades: ISelectOption[];
}

export const TabComposicao: React.FC<TabComposicaoProps> = ({
  kit,
  onChange,
  unidades,
}) => {
  // FASE 05 — 6ª validação/PO 2026-08-11: busca por termo (Perfil/Componente), como em
  // Perfis > Composição. Uma linha é editada por vez → estado de opções compartilhado.
  const [opcoesBusca, setOpcoesBusca] = useState<IOpcaoComposicao[]>([]);
  const [carregandoBusca, setCarregandoBusca] = useState(false);

  const buscar = useCallback(
    debounce(async (termo: string, tipoProduto: string) => {
      if (!termo || termo.length < 2) {
        setOpcoesBusca([]);
        return;
      }
      setCarregandoBusca(true);
      try {
        const opcoes =
          tipoProduto === "Perfil"
            ? await buscarPerfisComposicao(termo)
            : await buscarComponentesComposicao(termo);
        setOpcoesBusca(opcoes);
      } catch (error) {
        console.error("Erro ao buscar produtos para composição:", error);
        setOpcoesBusca([]);
      } finally {
        setCarregandoBusca(false);
      }
    }, 300),
    []
  );

  const handleComposicaoChange = (
    index: number,
    campo: keyof IKitComposicao,
    valor: any
  ) => {
    const composicao = [...(kit.composicao || [])];
    composicao[index] = { ...composicao[index], [campo]: valor };
    onChange({ composicao });
  };

  const handleTipoProdutoChange = (index: number, tipoProduto: string) => {
    const composicao = [...(kit.composicao || [])];
    composicao[index] = {
      ...composicao[index],
      tipoProduto: tipoProduto as "Perfil" | "Componente" | "Livre",
      codigo: "",
      descricao: "",
      idPerfil: undefined,
      idComponente: undefined,
    };
    onChange({ composicao });
    setOpcoesBusca([]);
  };

  // Digitação livre no campo Código (dispara a busca quando é Perfil/Componente).
  const handleCodigoInput = (index: number, valor: string) => {
    const composicao = [...(kit.composicao || [])];
    const item = composicao[index];
    composicao[index] = { ...item, codigo: valor };
    onChange({ composicao });
    if (item.tipoProduto !== "Livre") {
      buscar(valor, item.tipoProduto);
    }
  };

  // Seleção de uma opção do autocomplete: preenche código, descrição e o id certo.
  const handleSelecionarProduto = (index: number, opcao: IOpcaoComposicao | null) => {
    if (!opcao) return;
    const composicao = [...(kit.composicao || [])];
    const item = composicao[index];
    composicao[index] = {
      ...item,
      codigo: opcao.codigo,
      descricao: opcao.descricao,
      idPerfil: item.tipoProduto === "Perfil" ? opcao.id : undefined,
      idComponente: item.tipoProduto === "Componente" ? opcao.id : undefined,
    };
    onChange({ composicao });
  };

  const handleAdicionarProduto = () => {
    const composicao = [...(kit.composicao || [])];
    composicao.push({
      tipoProduto: "Livre",
      codigo: "",
      descricao: "",
      idUnidade: 0,
      quantidade: 1,
      defVariaveisUsinagem: "",
      ordem: composicao.length,
    });
    onChange({ composicao });
  };

  const handleRemoverProduto = (index: number) => {
    const composicao = [...(kit.composicao || [])];
    composicao.splice(index, 1);
    onChange({ composicao });
  };

  const validarQuantidade = (valor: string): number => {
    const num = parseInt(valor);
    if (isNaN(num) || num < 1) return 1;
    if (num > 999) return 999;
    return num;
  };

  return (
    <Paper sx={{ p: 2 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6">Composição</Typography>
        <Button
          startIcon={<AddIcon />}
          onClick={handleAdicionarProduto}
          variant="outlined"
          sx={{ bgcolor: "#3C90AD", color: "white", "&:hover": { bgcolor: "#2d6f87" } }}
        >
          ADICIONAR PRODUTO +
        </Button>
      </Box>

      <TableContainer sx={{ maxHeight: 600, overflowY: "auto" }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
              <TableCell sx={{ fontWeight: 600, minWidth: 120 }}>Produto</TableCell>
              <TableCell sx={{ fontWeight: 600, minWidth: 180 }}>Código</TableCell>
              <TableCell sx={{ fontWeight: 600, minWidth: 200 }}>Descrição</TableCell>
              <TableCell sx={{ fontWeight: 600, minWidth: 120 }}>Unidade</TableCell>
              <TableCell sx={{ fontWeight: 600, minWidth: 100 }}>Quantidade</TableCell>
              <TableCell sx={{ fontWeight: 600, minWidth: 200 }}>
                Def. variáveis / Usinagem
              </TableCell>
              <TableCell sx={{ fontWeight: 600, width: 50 }}></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(kit.composicao || []).map((item, index) => (
              <TableRow key={index} sx={{ "&:nth-of-type(odd)": { backgroundColor: "#fafafa" } }}>
                {/* Produto */}
                <TableCell>
                  <FormControl fullWidth size="small" required>
                    <Select
                      value={item.tipoProduto || "Livre"}
                      onChange={(e) => handleTipoProdutoChange(index, e.target.value)}
                    >
                      {TIPO_PRODUTO_OPTIONS.map((opcao) => (
                        <MenuItem key={opcao.id} value={opcao.id}>
                          {opcao.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </TableCell>

                {/* Código */}
                <TableCell>
                  {item.tipoProduto === "Livre" ? (
                    <TextField
                      fullWidth
                      required
                      size="small"
                      value={item.codigo || ""}
                      onChange={(e) => handleComposicaoChange(index, "codigo", e.target.value)}
                      placeholder="Código..."
                    />
                  ) : (
                    <Autocomplete
                      freeSolo
                      size="small"
                      options={opcoesBusca}
                      getOptionLabel={(option) =>
                        typeof option === "string" ? option : option.codigo || ""
                      }
                      inputValue={item.codigo || ""}
                      onInputChange={(_, newValue, reason) => {
                        if (reason === "input") {
                          handleCodigoInput(index, (newValue || "").toUpperCase());
                        }
                      }}
                      onChange={(_, newValue) => {
                        if (newValue && typeof newValue !== "string") {
                          handleSelecionarProduto(index, newValue);
                        }
                      }}
                      loading={carregandoBusca}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          required
                          placeholder={item.tipoProduto === "Perfil" ? "Buscar perfil..." : "Buscar componente..."}
                        />
                      )}
                      renderOption={(props, option) => (
                        <Box component="li" {...props} key={option.id}>
                          <Box>
                            <Typography variant="body2" fontWeight={500}>
                              {option.codigo}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {option.descricao}
                            </Typography>
                          </Box>
                        </Box>
                      )}
                    />
                  )}
                </TableCell>

                {/* Descrição */}
                <TableCell>
                  <TextField
                    fullWidth
                    required
                    size="small"
                    value={item.descricao || ""}
                    onChange={(e) => handleComposicaoChange(index, "descricao", e.target.value)}
                    placeholder="Descrição..."
                    inputProps={{ maxLength: 200 }}
                    disabled={item.tipoProduto !== "Livre" && !!item.codigo}
                  />
                </TableCell>

                {/* Unidade */}
                <TableCell>
                  <FormControl fullWidth size="small" required>
                    <Select
                      value={item.idUnidade || ""}
                      onChange={(e) => handleComposicaoChange(index, "idUnidade", parseInt(String(e.target.value)) || 0)}
                    >
                      <MenuItem value={0}>Selecione...</MenuItem>
                      {unidades.map((unidade) => (
                        <MenuItem key={unidade.id} value={unidade.id}>
                          {unidade.nome}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </TableCell>

                {/* Quantidade */}
                <TableCell>
                  <TextField
                    fullWidth
                    required
                    type="number"
                    size="small"
                    value={item.quantidade || ""}
                    onChange={(e) =>
                      handleComposicaoChange(index, "quantidade", validarQuantidade(e.target.value))
                    }
                    inputProps={{ min: 1, max: 999 }}
                    placeholder="Quantidade"
                  />
                </TableCell>

                {/* Def. variáveis / Usinagem */}
                <TableCell>
                  <TextField
                    fullWidth
                    size="small"
                    value={item.defVariaveisUsinagem || ""}
                    onChange={(e) =>
                      handleComposicaoChange(index, "defVariaveisUsinagem", e.target.value)
                    }
                    placeholder="Ex.: COMP=C001"
                    inputProps={{ maxLength: 500 }}
                  />
                </TableCell>

                {/* Remover */}
                <TableCell>
                  <IconButton
                    onClick={() => handleRemoverProduto(index)}
                    color="error"
                    size="small"
                  >
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {(!kit.composicao || kit.composicao.length === 0) && (
        <Box sx={{ textAlign: "center", py: 4 }}>
          <Typography variant="body2" color="text.secondary">
            Nenhum produto na composição. Clique em "ADICIONAR PRODUTO +" para começar.
          </Typography>
        </Box>
      )}
    </Paper>
  );
};
